import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import {
  OnChangeBusinessVariableHandler,
  BusinessVariableSettings,
} from './BusinessVariableSettings';
import {
  StyledAddVariableButton,
  StyledBottomButtons,
  StyledBusinessVariablesList,
} from './styles';
import { BusinessVariableErrors } from './useErrors';
import { BusinessVariable } from 'components/WYSIWYG/use-merge-fields';

type BusinessVariablesListProps = {
  data: BusinessVariable[];
  setData: Dispatch<SetStateAction<BusinessVariable[]>>;
  errors: BusinessVariableErrors;
  setErrors: Dispatch<SetStateAction<BusinessVariableErrors>>;
};

export const BusinessVariablesList = ({
  data,
  setData,
  errors,
  setErrors,
}: BusinessVariablesListProps) => {
  const { t } = useTranslation();

  const onChangeVariableSettings: OnChangeBusinessVariableHandler = ({
    id,
    prop,
    value,
  }) => {
    value = (value || '').trim();
    setData((prev) =>
      (prev || []).map((variable) => {
        if (variable.id === id) {
          return {
            ...variable,
            [prop]: value,
          };
        }
        return variable;
      })
    );

    return value;
  };

  const onDeleteVariable = (id: string, index: number) => {
    setData((prev) => (prev || []).filter((variable) => variable.id !== id));
    setErrors((prev) => ({
      ...prev,
      business_variables: (prev.business_variables || []).filter(
        (_, i) => index !== i
      ),
    }));
  };

  const onAddVariable = useCallback(() => {
    setData((prev) => [
      ...(prev || []),
      {
        id: uuidv4(),
        name: '',
        value_string: '',
      },
    ]);
  }, [setData]);

  const [variableIndexToScroll, setVariableIndexToScroll] = useState<
    number | null
  >(null);

  return (
    <>
      <StyledBusinessVariablesList>
        {(data || []).map((variable, i) => {
          return (
            <BusinessVariableSettings
              key={variable.id}
              index={i}
              variable={variable}
              onChange={onChangeVariableSettings}
              onDelete={onDeleteVariable}
              indexToScroll={variableIndexToScroll}
              setIndexToScroll={setVariableIndexToScroll}
              errors={errors}
              setErrors={setErrors}
            />
          );
        })}
      </StyledBusinessVariablesList>
      <StyledBottomButtons>
        <StyledAddVariableButton
          variant="text"
          onClick={onAddVariable}
          data-qa="add-business-variable-button"
        >
          {t('+  Add Variable')}
        </StyledAddVariableButton>
      </StyledBottomButtons>
    </>
  );
};
